import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSurveyQuery } from 'src/redux/survey/survey.endpoints';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import BorderedSelect from 'src/components/form-new/bordered-select';
import { Anchor, ArrowLeft, GitMerge, Link2, Tag } from 'lucide-react';
import type { Survey } from 'src/redux/survey/survey.types';
import type { SurveyFull } from 'src/redux/survey/survey.types';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'src/redux/redux-store';
import { persistSurveyGroupToApi } from 'src/services/survey-groups/persist-survey-group-to-api';
import {
  buildPerSurveyMapping,
  createSurveyGroupDraft,
  type AnswerMapState,
} from './build-survey-group';
import {
  SUGGESTION_MIN_CONFIDENCE,
  suggestMemberAnswerForAnchor,
  suggestMemberQuestionForAnchor,
} from '../utils/survey-group-fuzzy';
import { CombineSurveySelectStep } from './combine-survey-select-step';
import { CombineProgressSteps } from './combine-progress-steps';

type Phase = 'select' | 'anchor' | 'questions' | 'answers' | 'review';

/** Renders title with optional image (image survey questions / answers in combine wizard). */
function MediaWithTitle({
  title,
  imageUrl,
  size = 'md',
}: {
  title: string;
  imageUrl?: string | null;
  size?: 'sm' | 'md';
}): React.ReactElement {
  const src = imageUrl?.trim();
  const imgCls = size === 'sm' ? 'max-h-24 max-w-[120px]' : 'max-h-32 max-w-[160px]';
  if (!src) {
    return (
      <span className="break-words">
        {title}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
      <img
        src={src}
        alt=""
        className={`shrink-0 rounded-md border border-gray-200 bg-gray-50 object-contain ${imgCls}`}
        loading="lazy"
      />
      <span className="break-words">
        {title}
      </span>
    </div>
  );
}

const CombineSurveysPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const base = clientId ? `/client/${clientId}/surveys` : '/surveys';

  const surveyByIdRef = useRef<Record<string, Survey>>({});

  const [phase, setPhase] = useState<Phase>('select');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [memberStep, setMemberStep] = useState(0);
  const [questionMaps, setQuestionMaps] = useState<Record<string, Record<string, string | null>>>({});
  const [answerMaps, setAnswerMaps] = useState<Record<string, AnswerMapState>>({});
  const [groupName, setGroupName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const memberSurveyCache = useRef<Record<string, SurveyFull>>({});
  const questionAutoApplyMemberRef = useRef<string | null>(null);
  const answerAutoApplyMemberRef = useRef<string | null>(null);

  const members = useMemo(() => {
    if (!anchorId) return [];
    return selectedIds.filter((id) => id !== anchorId);
  }, [selectedIds, anchorId]);

  const currentMemberId = members[memberStep] ?? null;

  const { data: anchorSurvey } = useGetSurveyQuery(
    { ext_survey_id: anchorId ?? '' },
    { skip: !anchorId }
  );
  const { data: memberSurvey } = useGetSurveyQuery(
    { ext_survey_id: currentMemberId ?? '' },
    { skip: !currentMemberId || (phase !== 'questions' && phase !== 'answers') }
  );

  /** Avoid blank screen when phase/memberStep changes: RTK can briefly clear `data` while refetching. */
  const memberSurveyResolved: SurveyFull | undefined =
    memberSurvey ?? (currentMemberId ? memberSurveyCache.current[currentMemberId] : undefined);

  useEffect(() => {
    if (memberSurvey && currentMemberId && (phase === 'questions' || phase === 'answers')) {
      memberSurveyCache.current[currentMemberId] = memberSurvey;
    }
  }, [memberSurvey, currentMemberId, phase]);

  useEffect(() => {
    if (phase !== 'questions') {
      questionAutoApplyMemberRef.current = null;
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'answers') {
      answerAutoApplyMemberRef.current = null;
    }
  }, [phase]);

  /** Auto-fill question mappings when confidence ≥ threshold (once per member per visit). */
  useEffect(() => {
    if (phase !== 'questions' || !anchorSurvey || !memberSurveyResolved || !currentMemberId) {
      return;
    }
    if (questionAutoApplyMemberRef.current === currentMemberId) {
      return;
    }
    const next: Record<string, string | null> = {};
    for (const aq of anchorSurvey.questions) {
      const hints = suggestMemberQuestionForAnchor(aq, memberSurveyResolved.questions, 1);
      const best = hints[0];
      next[aq.id] =
        best && best.score >= SUGGESTION_MIN_CONFIDENCE ? best.memberQuestionId : null;
    }
    setQuestionMaps((prev) => ({ ...prev, [currentMemberId]: next }));
    questionAutoApplyMemberRef.current = currentMemberId;
  }, [phase, currentMemberId, anchorSurvey, memberSurveyResolved]);

  /** Auto-fill answer mappings when confidence ≥ threshold (once per member per visit). */
  useEffect(() => {
    if (phase !== 'answers' || !anchorSurvey || !memberSurveyResolved || !currentMemberId) {
      return;
    }
    if (answerAutoApplyMemberRef.current === currentMemberId) {
      return;
    }
    const qMap = questionMaps[currentMemberId] ?? {};
    setAnswerMaps((prev) => {
      const cur = prev[currentMemberId] ?? { byKey: {} };
      const nextByKey = { ...cur.byKey };
      for (const aq of anchorSurvey.questions) {
        const mqId = qMap[aq.id];
        if (!mqId) {
          continue;
        }
        const mq = memberSurveyResolved.questions.find((q) => q.id === mqId);
        if (!mq) {
          continue;
        }
        for (const aa of aq.answers) {
          const key = `${aq.id}__${aa.id}`;
          const hints = suggestMemberAnswerForAnchor(aa, mq.answers, 1);
          const best = hints[0];
          nextByKey[key] = {
            memberAnswerId:
              best && best.score >= SUGGESTION_MIN_CONFIDENCE ? best.memberAnswerId : null,
            displayLabelOverride: nextByKey[key]?.displayLabelOverride,
          };
        }
      }
      return {
        ...prev,
        [currentMemberId]: { byKey: nextByKey },
      };
    });
    answerAutoApplyMemberRef.current = currentMemberId;
  }, [phase, currentMemberId, anchorSurvey, memberSurveyResolved, questionMaps]);

  const selectedSurveysForAnchor = useMemo(
    () => selectedIds.map((id) => surveyByIdRef.current[id]).filter((s): s is Survey => Boolean(s)),
    [selectedIds]
  );

  const toggleSurvey = (survey: Survey) => {
    const id = survey.extSurveyId;
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        delete surveyByIdRef.current[id];
        return prev.filter((x) => x !== id);
      }
      surveyByIdRef.current[id] = survey;
      return [...prev, id];
    });
  };

  const goBack = () => {
    if (phase === 'select') {
      navigate(base);
      return;
    }
    if (phase === 'anchor') {
      setPhase('select');
      return;
    }
    if (phase === 'questions') {
      if (memberStep > 0) {
        setMemberStep((s) => s - 1);
      } else {
        setPhase('anchor');
      }
      return;
    }
    if (phase === 'answers') {
      if (memberStep > 0) {
        setMemberStep((s) => s - 1);
      } else {
        setPhase('questions');
        setMemberStep(Math.max(0, members.length - 1));
      }
      return;
    }
    if (phase === 'review') {
      setPhase('answers');
      setMemberStep(Math.max(0, members.length - 1));
    }
  };

  const advanceFromQuestions = () => {
    if (memberStep < members.length - 1) {
      setMemberStep((s) => s + 1);
    } else {
      setMemberStep(0);
      setPhase('answers');
    }
  };

  const advanceFromAnswers = () => {
    if (memberStep < members.length - 1) {
      setMemberStep((s) => s + 1);
    } else {
      setPhase('review');
    }
  };

  const setQuestionMap = (memberExt: string, anchorQ: string, memberQ: string | null) => {
    setQuestionMaps((prev) => ({
      ...prev,
      [memberExt]: { ...(prev[memberExt] ?? {}), [anchorQ]: memberQ },
    }));
  };

  const setAnswerCell = (
    memberExt: string,
    anchorQId: string,
    anchorAId: string,
    memberAnswerId: string | null,
    label?: string
  ) => {
    const key = `${anchorQId}__${anchorAId}`;
    setAnswerMaps((prev) => {
      const cur = prev[memberExt] ?? { byKey: {} };
      return {
        ...prev,
        [memberExt]: {
          byKey: {
            ...cur.byKey,
            [key]: { memberAnswerId, displayLabelOverride: label },
          },
        },
      };
    });
  };

  const handleSave = async() => {
    if (!anchorId || !anchorSurvey) return;
    setIsSaving(true);
    const cache = memberSurveyCache.current;
    const mappings = members.map((mId) => {
      const mq = cache[mId];
      const memberQs = mq?.questions ?? [];
      return buildPerSurveyMapping(
        anchorSurvey.questions,
        memberQs,
        mId,
        { byAnchorQuestionId: questionMaps[mId] ?? {} },
        answerMaps
      );
    });
    const group = createSurveyGroupDraft({
      name: groupName.trim() || 'Untitled group',
      anchorExtSurveyId: anchorId,
      memberExtSurveyIds: members,
      mappings,
    });
    try {
      await persistSurveyGroupToApi(dispatch, { group, anchorSurvey });
      navigate(base);
    } catch {
      /* errors surfaced by API base query */
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-none space-y-8">
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold">
          Combine surveys
        </h1>
      </div>

      <div className="w-full overflow-visible rounded-lg border border-gray-200 bg-white px-4 py-3 sm:px-5 sm:py-4">
        <CombineProgressSteps
          phase={phase}
          memberStepIndex={memberStep}
          memberCount={members.length}
        />
      </div>

      {phase === 'select' && (
        <CombineSurveySelectStep
          selectedIds={selectedIds}
          onToggleSurvey={toggleSurvey}
          onNext={() => {
            if (selectedIds.length >= 2) {
              setPhase('anchor');
            }
          }}
        />
      )}

      {phase === 'anchor' && (
        <AnchorStep
          selectedIds={selectedIds}
          surveys={selectedSurveysForAnchor}
          anchorId={anchorId}
          setAnchorId={setAnchorId}
          onNext={() => {
            if (anchorId) {
              setMemberStep(0);
              memberSurveyCache.current = {};
              setPhase('questions');
            }
          }}
        />
      )}

      {phase === 'questions' && anchorSurvey && memberSurveyResolved && currentMemberId && (
        <QuestionsStep
          anchorSurvey={anchorSurvey}
          memberSurvey={memberSurveyResolved}
          values={questionMaps[currentMemberId] ?? {}}
          onChange={(anchorQ, memberQ) => setQuestionMap(currentMemberId, anchorQ, memberQ)}
          stepLabel={`${memberStep + 1} of ${members.length}`}
          onNext={advanceFromQuestions}
        />
      )}

      {phase === 'answers' && anchorSurvey && memberSurveyResolved && currentMemberId && (
        <AnswersStep
          anchorSurvey={anchorSurvey}
          memberSurvey={memberSurveyResolved}
          questionMap={questionMaps[currentMemberId] ?? {}}
          answerState={answerMaps[currentMemberId] ?? { byKey: {} }}
          onAnswerChange={(anchorQId, anchorAId, memberAId, label) =>
            setAnswerCell(currentMemberId, anchorQId, anchorAId, memberAId, label)
          }
          stepLabel={`${memberStep + 1} of ${members.length}`}
          onNext={advanceFromAnswers}
        />
      )}

      {phase === 'review' && anchorSurvey && (
        <div className="w-full space-y-4">
          <div className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white shadow-md">
            <div className="flex items-center gap-2 border-b border-gray-200/80 bg-gray-201 px-4 py-3">
              <Tag
                className="h-4 w-4 shrink-0"
                aria-hidden
              />
              <span className="text-sm font-medium">
                Name survey group
              </span>
            </div>
            <div className="space-y-5 p-6">
              <p className="text-sm text-gray-600">
                Give this survey group a clear name, then save.
              </p>
              <div className="space-y-2">
                <label
                  htmlFor="group-name"
                  className="text-sm font-medium"
                >
                  Group name
                </label>
                <Input
                  id="group-name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Q1 2025 tracker"
                />
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3 text-sm text-gray-700">
                <p>
                  <span className="font-medium text-gray-900">
                    Anchor:
                  </span>
                  {' '}
                  {anchorSurvey.title}
                </p>
                <p className="mt-1">
                  <span className="font-medium text-gray-900">
                    Members:
                  </span>
                  {' '}
                  {members.length}
                </p>
              </div>
              <Button
                type="button"
                disabled={isSaving}
                onClick={handleSave}
              >
                Save survey group
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function AnchorStep(props: {
  selectedIds: string[];
  surveys: Survey[];
  anchorId: string | null;
  setAnchorId: (id: string) => void;
  onNext: () => void;
}) {
  const { selectedIds, surveys, anchorId, setAnchorId, onNext } = props;
  const selected = surveys.filter((s) => selectedIds.includes(s.extSurveyId));
  return (
    <div className="w-full space-y-4">
      <div className="w-full bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-2 bg-gray-201 px-4 py-3 border-b border-gray-200/80">
          <Anchor
            className="h-4 w-4 shrink-0"
            aria-hidden
          />
          <span className="font-medium text-sm">
            Choose anchor survey
          </span>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600">
            Choose the anchor survey. All others map to this survey’s questions and answers.
          </p>
          <div className="space-y-3">
            {selected.map((s) => (
              <label
                key={s.extSurveyId}
                className="flex items-start gap-3 cursor-pointer rounded-lg border border-transparent px-3 py-2 -mx-3 hover:bg-gray-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-400"
              >
                <input
                  type="radio"
                  name="anchor"
                  checked={anchorId === s.extSurveyId}
                  onChange={() => setAnchorId(s.extSurveyId)}
                  className="accent-blue-600 mt-1 shrink-0"
                />
                <span className="text-sm font-medium text-gray-900 leading-snug">
                  {s.title}
                </span>
              </label>
            ))}
          </div>
          <div className="pt-1">
            <Button
              type="button"
              disabled={!anchorId}
              onClick={onNext}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionsStep(props: {
  anchorSurvey: SurveyFull;
  memberSurvey: SurveyFull;
  values: Record<string, string | null>;
  onChange: (anchorQ: string, memberQ: string | null) => void;
  stepLabel: string;
  onNext: () => void;
}) {
  const {
    anchorSurvey,
    memberSurvey,
    values,
    onChange,
    stepLabel,
    onNext,
  } = props;

  const memberOptions = useMemo(
    () => [
      { value: '', label: 'Not mapped' },
      ...memberSurvey.questions.map((mq) => ({
        value: mq.id,
        label: mq.title,
        imageUrl: mq.imageUrl,
      })),
    ],
    [memberSurvey.questions]
  );

  return (
    <div className="w-full space-y-4">
      <div className="w-full bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-gray-200/80 bg-gray-201 px-4 py-3">
          <GitMerge
            className="h-4 w-4 shrink-0"
            aria-hidden
          />
          <span className="text-sm font-medium">
            Map questions (
            {stepLabel}
            )
          </span>
        </div>
        <div className="space-y-6 p-6">
          <p className="text-sm text-gray-600">
            Link each anchor question to a question in the member survey. Rows with a fuzzy match at
            {' '}
            {Math.round(SUGGESTION_MIN_CONFIDENCE * 100)}
            % confidence or higher are filled in automatically; change any row as needed.
          </p>
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="h-auto w-[36%] align-top py-3">
                  Anchor question
                </TableHead>
                <TableHead className="h-auto w-[14%] align-top py-3 whitespace-nowrap">
                  Type
                </TableHead>
                <TableHead className="h-auto min-w-0 w-[50%] align-top py-3">
                  Maps to (member)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {anchorSurvey.questions.map((aq) => (
                <TableRow key={aq.id}>
                  <TableCell className="min-w-0 align-top break-words py-3">
                    <MediaWithTitle
                      title={aq.title}
                      imageUrl={aq.imageUrl}
                    />
                  </TableCell>
                  <TableCell className="align-top py-3 text-sm whitespace-nowrap">
                    {aq.type}
                  </TableCell>
                  <TableCell className="min-w-0 align-top py-3">
                    <div className="w-full min-w-0 max-w-full">
                      <BorderedSelect
                        options={memberOptions}
                        value={values[aq.id] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          onChange(aq.id, v === '' ? null : v);
                        }}
                        placeholder="Not mapped"
                        searchEnabled
                        fullWidth
                        containerMaxWidth="100%"
                        textClassName="break-words text-left text-gray-900"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button
            type="button"
            onClick={onNext}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

function AnswersStep(props: {
  anchorSurvey: SurveyFull;
  memberSurvey: SurveyFull;
  questionMap: Record<string, string | null>;
  answerState: AnswerMapState;
  onAnswerChange: (anchorQId: string, anchorAId: string, memberAId: string | null, label?: string) => void;
  stepLabel: string;
  onNext: () => void;
}) {
  const {
    anchorSurvey,
    memberSurvey,
    questionMap,
    answerState,
    onAnswerChange,
    stepLabel,
    onNext,
  } = props;

  const pairs = anchorSurvey.questions
    .map((aq) => {
      const mqId = questionMap[aq.id];
      if (!mqId) return null;
      const mq = memberSurvey.questions.find((q) => q.id === mqId);
      if (!mq) return null;
      return { aq, mq };
    })
    .filter(Boolean) as { aq: SurveyFull['questions'][0]; mq: SurveyFull['questions'][0] }[];

  return (
    <div className="w-full space-y-4">
      <div className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white shadow-md">
        <div className="flex items-center gap-2 border-b border-gray-200/80 bg-gray-201 px-4 py-3">
          <Link2
            className="h-4 w-4 shrink-0"
            aria-hidden
          />
          <span className="text-sm font-medium">
            Map answers (
            {stepLabel}
            )
          </span>
        </div>
        <div className="space-y-8 p-6">
          <p className="text-sm text-gray-600">
            For each linked question below, map anchor answers to member answers. Rows with a fuzzy match at
            {' '}
            {Math.round(SUGGESTION_MIN_CONFIDENCE * 100)}
            % confidence or higher are filled in automatically; add a label when the wording still differs.
          </p>

          {pairs.length === 0 ? (
            <p className="text-sm text-gray-500">
              No linked questions with answers to map.
            </p>
          ) : (
            pairs.map(({ aq, mq }) => {
              const answerOpts = [
                { value: '', label: 'Not mapped' },
                ...mq.answers.map((ma) => ({
                  value: ma.id,
                  label: ma.title,
                  imageUrl: ma.imageUrl,
                })),
              ];
              return (
                <div
                  key={aq.id}
                  className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                >
                  <div className="border-b border-gray-200/80 bg-gray-201 px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Question
                    </p>
                    <div className="text-sm font-medium text-gray-900">
                      <MediaWithTitle
                        title={aq.title}
                        imageUrl={aq.imageUrl}
                      />
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <Table className="w-full table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-auto w-[32%] align-top py-3">
                            Anchor answer
                          </TableHead>
                          <TableHead className="h-auto min-w-0 w-[40%] align-top py-3">
                            Member answer
                          </TableHead>
                          <TableHead className="h-auto min-w-0 w-[28%] align-top py-3">
                            Label if different
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aq.answers.map((aa) => {
                          const key = `${aq.id}__${aa.id}`;
                          const row = answerState.byKey[key];
                          return (
                            <TableRow key={aa.id}>
                              <TableCell className="min-w-0 align-top break-words py-3 text-sm">
                                <MediaWithTitle
                                  title={aa.title}
                                  imageUrl={aa.imageUrl}
                                  size="sm"
                                />
                              </TableCell>
                              <TableCell className="min-w-0 align-top py-3">
                                <div className="w-full min-w-0 max-w-full">
                                  <BorderedSelect
                                    options={answerOpts}
                                    value={row?.memberAnswerId ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      onAnswerChange(aq.id, aa.id, v === '' ? null : v, row?.displayLabelOverride);
                                    }}
                                    placeholder="Map"
                                    searchEnabled
                                    fullWidth
                                    containerMaxWidth="100%"
                                    textClassName="break-words text-left text-gray-900"
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="min-w-0 align-top py-3">
                                <Input
                                  className="w-full min-w-0"
                                  placeholder="Optional"
                                  value={row?.displayLabelOverride ?? ''}
                                  onChange={(e) =>
                                    onAnswerChange(aq.id, aa.id, row?.memberAnswerId ?? null, e.target.value)
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              );
            })
          )}

          <Button
            type="button"
            onClick={onNext}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CombineSurveysPage;
